import { RefObject, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from '@digital-twins/design-system/icons'

import { CountryFlag } from '@/components/ui/CountryFlag'
import { cn } from '@/lib/utils'
import { marketDisplayName, marketFlagCode } from '@/pages/KnowledgeHubPage/libraryHelpers'
import { IDLE_PILL_CLASS, SocialFilterToken } from '@/pages/KnowledgeHubPage/social/SocialFilterPill'
import { SocialFilterMenu } from '@/pages/KnowledgeHubPage/social/SocialFilterPopover'
import {
  CollectionFacetFilterKey,
  CollectionFacetOptions,
  CollectionResourceFilters,
  LibraryFacetOption,
} from '@/pages/KnowledgeCollectionPage/useCollectionFilters'

export interface CollectionFilterBarProps {
  filters: CollectionResourceFilters
  /** From `useLibraryFacetOptions().filterOptions` (AB6 tokens). */
  options: CollectionFacetOptions
  /** Toggles one option: every token behind its value text. */
  onToggleOption: (key: CollectionFacetFilterKey, tokens: Array<string>) => void
  onRemoveOption: (key: CollectionFacetFilterKey, tokens: Array<string>) => void
  onClear: () => void
  /** The `aria-live` count ("{n} resources"); null while loading. */
  resultCount: number | null
}

const FILTER_KEYS: Array<CollectionFacetFilterKey> = ['market', 'year', 'brand']

/** An active value of one facet: an option, or (label null) the set tokens no option names. */
export interface CollectionActiveFacetValue {
  label: string | null
  tokens: Array<string>
}

export interface CollectionFacetValuesSplit {
  active: Array<CollectionActiveFacetValue>
  idle: Array<LibraryFacetOption>
}

/**
 * Splits a facet's options into active ones (any of their tokens set) and
 * idle ones. Set tokens no option names (a stale URL) form one last active
 * value once the options have loaded, so they stay removable.
 */
export function splitFacetValues(
  selected: Array<string>,
  options: Array<LibraryFacetOption>,
): CollectionFacetValuesSplit {
  const set = new Set(selected)
  const named = new Set<string>()
  const active: Array<CollectionActiveFacetValue> = []
  const idle: Array<LibraryFacetOption> = []
  for (const option of options) {
    const hits = option.tokens.filter((token) => set.has(token))
    if (hits.length === 0) {
      idle.push(option)
      continue
    }
    hits.forEach((token) => named.add(token))
    active.push({ label: option.value, tokens: option.tokens })
  }
  const unnamed = selected.filter((token) => !named.has(token))
  if (unnamed.length > 0 && options.length > 0) {
    active.push({ label: null, tokens: unnamed })
  }
  return { active, idle }
}

/** The nearest scrolling ancestor (the page shell's scroller); null for the viewport. */
function scrollParentOf(node: HTMLElement): HTMLElement | null {
  let parent = node.parentElement
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent
    }
    parent = parent.parentElement
  }
  return null
}

/**
 * Whether a `sticky top-[-1px]` element is stuck: one pixel of it then
 * sits above its scroller's top edge. False where IntersectionObserver is
 * missing (tests, old browsers), which draws the resting bar.
 */
function useIsStuck(ref: RefObject<HTMLElement>): boolean {
  const [isStuck, setIsStuck] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      return undefined
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        const rootTop = entry.rootBounds?.top ?? 0
        setIsStuck(entry.intersectionRatio < 1 && entry.boundingClientRect.top <= rootTop)
      },
      { root: scrollParentOf(node), threshold: [1] },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [ref])

  return isStuck
}

/**
 * The collection's sticky filter bar, in the Topic bar's language: a
 * hairline above and a near solid background, full bleed in the page
 * column so card images never show around it, with a soft bottom shadow
 * only while stuck (`data-stuck`), market, year and brand pills
 * with `SocialFilterMenu`, active values as `SocialFilterToken`s with ×,
 * Clear and the `aria-live` count. Picking a value sends every facet value
 * token behind its text. Option counts are Library-wide, so none is shown.
 */
export function CollectionFilterBar(props: CollectionFilterBarProps) {
  const { filters, options, onToggleOption, onRemoveOption, onClear, resultCount } = props

  const [openMenu, setOpenMenu] = useState<CollectionFacetFilterKey | null>(null)

  const barRef = useRef<HTMLDivElement>(null)
  const { t, i18n } = useTranslation('app')
  const isStuck = useIsStuck(barRef)

  const hasFilters = FILTER_KEYS.some((key) => filters[key].length > 0)

  const menuOpenChange = (key: CollectionFacetFilterKey) => (isOpen: boolean) => {
    setOpenMenu((current) => (isOpen ? key : current === key ? null : current))
  }

  const onClearAll = () => {
    setOpenMenu(null)
    onClear()
  }

  const facetControls = FILTER_KEYS.flatMap((key) => {
    const label = t(`library.collections.resources.filters.${key}`)
    const { active, idle } = splitFacetValues(filters[key], options[key])
    const testId = `collection-filter-${key}`

    const tokens = active.map((value, index) => {
      const shown = value.label == null ? null : key === 'market' ? marketDisplayName(value.label, i18n.language) : value.label
      return (
        <SocialFilterToken
          key={`${key}-token-${value.label ?? '\u0000unnamed'}`}
          label={label}
          value={
            shown == null ? null : key === 'market' ? (
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <CountryFlag code={marketFlagCode(value.label)} />
                <span className="truncate">{shown}</span>
              </span>
            ) : (
              <span className="truncate">{shown}</span>
            )
          }
          removeLabel={t('library.collections.resources.filters.remove', { value: shown ?? label })}
          onRemove={() => onRemoveOption(key, value.tokens)}
          testId={`${testId}-${index}`}
        />
      )
    })

    if (idle.length === 0) {
      return tokens
    }

    const pill = (
      <SocialFilterMenu
        key={`${key}-menu`}
        label={label}
        options={idle.map((option, index) => ({
          value: option.value,
          label:
            key === 'market' ? (
              <span className="inline-flex min-w-0 items-center gap-2">
                <CountryFlag code={marketFlagCode(option.value)} data-testid={`${testId}-option-${index}-flag`} />
                <span className="truncate">{marketDisplayName(option.value, i18n.language)}</span>
              </span>
            ) : (
              <span className="truncate">{option.value}</span>
            ),
          testId: `${testId}-option-${index}`,
        }))}
        selected={null}
        onSelect={(value) => {
          const option = idle.find((candidate) => candidate.value === value)
          if (option) {
            onToggleOption(key, option.tokens)
          }
        }}
        isOpen={openMenu === key}
        onOpenChange={menuOpenChange(key)}
        trigger={
          <button type="button" aria-haspopup="listbox" className={IDLE_PILL_CLASS} data-testid={testId}>
            {label}
            <ChevronDown
              className="size-3.5 text-muted-foreground transition-transform duration-200 group-aria-expanded:rotate-180"
              aria-hidden="true"
            />
          </button>
        }
        testId={`${testId}-menu`}
      />
    )

    return [...tokens, pill]
  })

  return (
    <div
      ref={barRef}
      className={cn(
        // On the content column, like the Topic page's smart bar: the hairline lines up with the cards.
        'sticky top-[-1px] z-30 border-t border-[hsl(var(--card-border))] bg-background/95 py-2.5 backdrop-blur-md backdrop-saturate-150 transition-shadow duration-200',
        isStuck ? 'border-b border-b-border shadow-[0_8px_16px_-12px_hsl(var(--foreground)/0.25)]' : 'border-b border-b-transparent',
      )}
      data-stuck={isStuck ? 'true' : 'false'}
      data-testid="collection-filter-bar"
    >
      <div
        role="toolbar"
        aria-label={t('library.collections.resources.filters.ariaLabel')}
        className="flex min-h-10 flex-wrap items-center gap-2"
      >
        <div className="flex min-w-0 grow flex-wrap items-center gap-1.5 p-0.5" data-testid="collection-filter-pills">
          {facetControls}
        </div>
        {hasFilters ? (
          <button
            type="button"
            className={cn(
              'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
            onClick={onClearAll}
            data-testid="collection-filters-clear"
          >
            {t('library.collections.clear')}
          </button>
        ) : null}
        {resultCount != null ? <span aria-hidden="true" className="h-5 w-px shrink-0 bg-border" /> : null}
        <span
          aria-live="polite"
          className="shrink-0 whitespace-nowrap px-1 text-sm tabular-nums"
          data-testid="collection-filters-count"
        >
          {resultCount != null ? t('library.collections.resources.filters.count', { count: resultCount }) : null}
        </span>
      </div>
    </div>
  )
}
